import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaCheckCircle } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/transferEntry";

const s = (v) => (v === null || v === undefined ? "" : String(v));

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

const fmtDMY = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const AssetTransferApproveReceive = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ localStorage data
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ Mode priority: state -> query -> sessionStorage -> default RECEIVE
  const mode = useMemo(() => {
    const stMode = String(location?.state?.mode || "").trim();
    if (stMode) return stMode.toUpperCase();

    const qs = new URLSearchParams(window.location.search);
    const qMode = String(qs.get("mode") || "").trim();
    if (qMode) return qMode.toUpperCase();

    try {
      const savedMode = sessionStorage.getItem("assetTransferMode");
      if (savedMode) return String(savedMode).toUpperCase();
    } catch (_) {}

    return "RECEIVE";
  }, [location]);

  // ✅ transferId priority: state -> query -> sessionStorage
  const transferId = useMemo(() => {
    const st =
      location?.state?.transferId ||
      location?.state?.transfer?.transferId ||
      location?.state?.raw?.TransferId ||
      location?.state?.raw?.fld_transferId ||
      location?.state?.raw?.fld_Asset_Transferid ||
      location?.state?.raw?.transferId;

    if (st) return String(st);

    const qs = new URLSearchParams(window.location.search);
    const qId = qs.get("transferId");
    if (qId) return String(qId);

    try {
      const saved = JSON.parse(
        sessionStorage.getItem("assetTransferSelected") || "null"
      );
      if (saved?.transferId) return String(saved.transferId);
    } catch (_) {}

    return "";
  }, [location]);

  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);

  const [branches, setBranches] = useState([]);
  const branchNameById = useMemo(() => {
    const map = new Map();
    (branches || []).forEach((b) => {
      const id = b?.fld_Branchid ?? b?.branchid ?? b?.BranchId;
      const name = b?.fld_BranchName ?? b?.branchName ?? b?.BranchName;
      if (id !== undefined && id !== null) {
        map.set(String(id), String(name || id));
      }
    });
    return map;
  }, [branches]);

  const [form, setForm] = useState({
    transferFrom: "",
    transferTo: "",
    assetType: "",
    assetSubType: "",
    idCode: "",
    description: "",
    assetModel: "",
    assetMake: "",
    serialNo: "",
    regNo: "",
    reasonForTransfer: "",
    transferDate: "",
    transferQty: "",
    approvalStatus: "",
    receiveStatus: "",
  });

  const isApproved = useMemo(() => {
    const st = String(form.approvalStatus || "").toLowerCase();
    return st === "approved" || st === "y" || st === "yes" || st === "true";
  }, [form.approvalStatus]);

  // ✅ init branches
  const loadInit = async () => {
    try {
      const res = await axios.get(`${API_BASE}/init`, {
        headers: commonHeaders,
        params: {
          loginuserid: userId,
          branchid: branchId,
          branchtype: branchType,
          finYearId,
          finYear,
        },
      });

      const arr = res?.data?.data?.branches || [];
      setBranches(Array.isArray(arr) ? arr : []);
    } catch (e) {
      console.error(e);
      setBranches([]);
    }
  };

  // ✅ MAIN: load transfer using API: /edit
  const loadTransfer = async () => {
    if (!transferId) {
      toast.error("Transfer Id not found.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/edit`, {
        headers: commonHeaders,
        params: {
          transferId,
          loginuserid: userId,
          branchtype: branchType,
          branchid: branchId,
          finYearId,
          finYear,
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to load transfer details");
        return;
      }

      const d = res?.data?.data || {};
      const rows = Array.isArray(d?.transferIdRows) ? d.transferIdRows : [];
      const firstRow = rows[0] || {};

      const idsCsv =
        pick(d, ["assetIds"], "") ||
        pick(firstRow, ["fld_Assetid"], "");

      const fromId = String(
        pick(d, ["fromBranchId"], "") ||
          pick(firstRow, ["fld_Frombranchid"], "")
      );

      const toId = String(
        pick(d, ["toBranchId"], "") ||
          pick(firstRow, ["fld_Tobranchid"], "")
      );

      const fromName = branchNameById.get(fromId) || fromId;
      const toName = branchNameById.get(toId) || toId;

      let assetDetails = Array.isArray(d?.assetDetails) ? d.assetDetails : [];

      if (assetDetails.length === 0 && idsCsv) {
        const detRes = await axios.get(`${API_BASE}/assetDetails`, {
          headers: commonHeaders,
          params: {
            assetIds: idsCsv,
            loginuserid: userId,
            branchid: branchId,
            branchtype: branchType,
            finYearId,
            finYear,
          },
        });

        if (detRes?.data?.success) {
          assetDetails = Array.isArray(detRes?.data?.data)
            ? detRes.data.data
            : [];
        }
      }

      const firstAsset = assetDetails[0] || {};

      const idCodes = assetDetails
        .map((a) =>
          pick(a, ["fld_idcodeno", "IdCode", "AssetIdCode", "idCode"], "")
        )
        .filter(Boolean)
        .join("\n");

      const descs = assetDetails
        .map((a) =>
          pick(a, ["fld_description", "Description", "assetDescription"], "")
        )
        .filter(Boolean)
        .join("\n");

      const model = pick(
        firstAsset,
        ["ModelName", "fld_model", "assetModel"],
        ""
      );
      const make = pick(
        firstAsset,
        ["MakeName", "fld_make", "assetMake"],
        ""
      );
      const serialNo = pick(
        firstAsset,
        ["fld_serialno", "SerialNo", "machineSrNo", "serialNo"],
        ""
      );
      const regNo = pick(
        firstAsset,
        ["fld_regno", "RegNo", "vehicleRegNo", "regNo"],
        ""
      );

      const qty = pick(firstRow, ["fld_transferqty"], "");

      const approvedFlag = firstRow?.fld_IsApproved;
      const approvalStatus =
        approvedFlag === true || approvedFlag === 1
          ? "Approved"
          : "Waiting for Approval";

      setForm({
        transferFrom: fromName,
        transferTo: toName,
        assetType: pick(
          firstAsset,
          ["AssetTypeName", "fld_assetType", "AssetType"],
          ""
        ),
        assetSubType: pick(
          firstAsset,
          ["AssetSubTypeName", "fld_assetSubType", "AssetSubType"],
          ""
        ),
        idCode: idCodes || s(idsCsv),
        description: descs,
        assetModel: model,
        assetMake: make,
        serialNo,
        regNo,
        reasonForTransfer:
          pick(d, ["transferReason"], "") ||
          pick(firstRow, ["fld_TransferReason"], ""),
        transferDate: fmtDMY(
          pick(d, ["transferDate"], "") ||
            pick(firstRow, ["fld_Transferdate"], "")
        ),
        transferQty: s(qty),
        approvalStatus,
        receiveStatus: pick(firstRow, ["fld_inwarddate"], "")
          ? "Received"
          : "",
      });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Error loading transfer details"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTransfer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferId, branches.length]);

  // ✅ Approve button -> calls POST /approve
  const handleApprove = async () => {
    if (!transferId) return toast.error("Transfer Id missing");
    if (approving) return;
    if (isApproved) return toast.info("Already Approved");

    try {
      setApproving(true);

      const payload = {
        transferId,
        loginuserid: userId,
        userId,
        branchid: branchId,
        branchtype: branchType,
        finYearId,
        finYear,
      };

      const res = await axios.post(`${API_BASE}/approve`, payload, {
        headers: commonHeaders,
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Approve failed");
        return;
      }

      toast.success(res?.data?.message || "Approved successfully");
      await loadTransfer();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Approve API error");
    } finally {
      setApproving(false);
    }
  };

  const Field = ({ label, value }) => (
    <div className="grid grid-cols-12 gap-3 items-start mb-4">
      <div
        className={`col-span-4 text-sm ${
          isDark ? "text-gray-300" : "text-gray-700"
        }`}
      >
        {label}
      </div>
      <div className="col-span-8">
        <div
          className={`min-h-[34px] px-3 py-2 rounded border ${
            isDark
              ? "bg-gray-800 border-gray-700 text-gray-100"
              : "bg-white border-gray-200 text-gray-900"
          }`}
        >
          {s(value)}
        </div>
      </div>
    </div>
  );

  const TextAreaField = ({ label, value }) => (
    <div className="grid grid-cols-12 gap-3 items-start mb-4">
      <div
        className={`col-span-4 text-sm ${
          isDark ? "text-gray-300" : "text-gray-700"
        }`}
      >
        {label}
      </div>
      <div className="col-span-8">
        <textarea
          value={s(value)}
          readOnly
          rows={3}
          className={`w-full px-3 py-2 rounded border resize-none ${
            isDark
              ? "bg-gray-800 border-gray-700 text-gray-100"
              : "bg-white border-gray-200 text-gray-900"
          }`}
        />
      </div>
    </div>
  );

  return (
    <AssetLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              ASSET TRANSFER RECEIVING
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />
          <button
              onClick={() => navigate("/Asset/AssetReceiveListing")}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold border ${
                isDark
                  ? "border-gray-700 text-gray-200 hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft /> Back
            </button>
        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black border-gray-200"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="text-sm">
              <span className={isDark ? "text-gray-300" : "text-gray-600"}>
                Transfer Id:{" "}
              </span>
              <span
                className={`${
                  isDark ? "text-white" : "text-gray-900"
                } font-semibold`}
              >
                {transferId || "-"}
              </span>
              <span
                className={`ml-3 px-2 py-1 rounded text-xs ${
                  mode === "APPROVE"
                    ? "bg-green-600 text-white"
                    : "bg-gray-500 text-white"
                }`}
              >
                MODE: {mode}
              </span>
            </div>

           
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <Field label="Transfer From" value={form.transferFrom} />
              <Field label="Asset Type" value={form.assetType} />
              <TextAreaField label="Id Code" value={form.idCode} />
              <Field label="Asset Model" value={form.assetModel} />
              <Field label="Serial No" value={form.serialNo} />
              <Field
                label="Reason for Transfer"
                value={form.reasonForTransfer}
              />
              <Field label="Transfer Qty" value={form.transferQty} />
            </div>

            <div>
              <Field label="Transfer To" value={form.transferTo} />
              <Field label="Asset Sub Type" value={form.assetSubType} />
              <TextAreaField label="Description" value={form.description} />
              <Field label="Asset Make" value={form.assetMake} />
              <Field label="Reg.No" value={form.regNo} />
              <Field label="Transfer Date" value={form.transferDate} />
              <Field label="Approval Status" value={form.approvalStatus} />
              <Field label="Receive Status" value={form.receiveStatus} />
            </div>
          </div>

          {mode === "APPROVE" && (
            <div className="flex justify-center gap-4 mt-10 flex-wrap">
              <button
                onClick={handleApprove}
                disabled={loading || approving || isApproved}
                className={`flex items-center gap-2 px-10 py-3 rounded-xl font-bold shadow-lg ${buttonClass} ${hoverClass} text-white ${
                  loading || approving || isApproved
                    ? "opacity-70 cursor-not-allowed"
                    : ""
                }`}
              >
                <FaCheckCircle />
                {isApproved
                  ? "Approved"
                  : approving
                  ? "Approving..."
                  : "Approve"}
              </button>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetTransferApproveReceive;